<template>

	<div class="container">
		<b-loading :is-full-page="true" v-model="loading" :can-cancel="false"></b-loading>
		<div v-if="!loading">
			<div class="columns has-text-centered">
				<div class="column is-10 is-offset-1">
					<h1 id="top">{{ portfolio.Name }} Portfolio report</h1>
				</div>
				<div class="column is-1">
					<b-dropdown aria-role="list" position="is-bottom-left">
						<template #trigger="{ active }">
							<b-button class="is-secondary has-text-right">Options &nbsp;<i class="fas fa-ellipsis-v"></i></b-button>
						</template>
						<b-dropdown-item aria-role="listitem"><a style="border: none" @click="$router.push('/portfolio/manage/' + portfolio.Id)">Manage Portfolio</a></b-dropdown-item>
						<b-dropdown-item aria-role="listitem"><download-csv :data="aircraftCSV" :name="$moment().format('DD-MM-YYYY') + '-' + portfolio.Name + '.csv'" :fields="['Name', 'Manufacturer', 'Aircraft model', 'AircraftType', 'Quantity', 'Average flight hours', 'Average Cycles', 'Aggregate Annual C02 emissions (tonnes)']">Download CSV</download-csv></b-dropdown-item>
						<b-dropdown-item aria-role="listitem">
							<portfolio-certificate Link @complete="setLoading(portfolio, false)" :Portfolio="portfolio"></portfolio-certificate>

						</b-dropdown-item>
					</b-dropdown>

				</div>
			</div>
			<p class="has-text-centered">Created: {{ $moment().format("DD MMMM, YYYY") }}</p>

			<div class="my-6">
				<h2>Report Sections</h2>
				<ul class="sections">
					<li><a href="#emissions">Aggregate annual CO2 emissions</a></li>
					<li><a href="#type">Analysis by aircraft type</a></li>
					<li><a href="#model">Analysis by aircraft model</a></li>
					<li><a href="#aircraft">Analysis by aircraft</a></li>
				</ul>
			</div>
			<a class="anchor" id="emissions"></a>
			<div class="my-6">
				<div class="level">
					<div class="level-left"><h2>Aggregate annual CO2 emissions</h2></div>
					<div class="level-right"><a class="is-pulled-right" href="#top">Back to top</a></div>
				</div>
				<div>
					<div class="has-text-centered my-5">
						<div class="is-flex is-justify-content-center">
							<div class="large-font is-blue">{{$math.format($math.round((portfolio.Emissions || 0), 3))}}</div>
							<div class="is-align-self-flex-end mb-2 ml-2">tonnes</div>
						</div>
					</div>
					<p class="has-text-centered">Actual CO2 emission may vary, please see the <a target="_blank" href="/Calculation Methodology Document.pdf">Calculation Methodology Document</a> for more details.</p>
				</div>
			</div>

			<a class="anchor" id="type"></a>
			<div class="my-6" id="type">
				<div class="level">
					<div class="level-left"><h2 class="my-6">Analysis by aircraft type</h2></div>
					<div class="level-right"><a class="is-pulled-right" href="#top">Back to top</a></div>
				</div>


				<div>
					<h3 class="is-blue has-text-centered">Portfolio by aircraft type</h3>
					<AircraftTable :Aircraft="portfolio.AircraftByType"
								   IsBlue
								   HideNumber
								   HideEdit
								   Type></AircraftTable>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionPieChart']" :disabled="exporting['aircraftByTypeEmissionPieChart']" @click="Export('aircraftByTypeEmissionPieChart')">Export chart</b-button>
				<div id="aircraftByTypeEmissionPieChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Portfolio by aircraft type of cumulative CO2 emissions</h3>
					<pie :Data="portfolio.AircraftByTypeEmissionPieChart" :Options="chartOptions.pie.typeEmissions.options"></pie>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionScatterChart']" :disabled="exporting['aircraftByTypeEmissionScatterChart']" @click="Export('aircraftByTypeEmissionScatterChart')">Export chart</b-button>
				<div id="aircraftByTypeEmissionScatterChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Portfolio by aircraft type showing aggregate annual CO2 emissions against average stage length</h3>
					<ScatterChart :Data="portfolio.AircraftByTypeEmissionScatterChart" :Options="chartOptions.scatter.typeEmissions.options"></ScatterChart>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByTypeEmissionBubbleChart']" :disabled="exporting['aircraftByTypeEmissionBubbleChart']" @click="Export('aircraftByTypeEmissionBubbleChart')">Export chart</b-button>
				<div id="aircraftByTypeEmissionBubbleChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Portfolio by aircraft type showing average cycles against average flight hours against aggregate annual CO2 emissions</h3>
					<bubble :Data="portfolio.AircraftByTypeEmissionBubbleChart" :Options="chartOptions.bubble.typeEmissions.options"></bubble>
				</div>
			</div>

			<a class="anchor" id="model"></a>
			<div class="my-6">
				<div class="level">
					<div class="level-left"><h2 class="my-6">Analysis by aircraft model</h2></div>
					<div class="level-right"><a class="is-pulled-right" href="#top">Back to top</a></div>
				</div>
				<h3 class="is-blue has-text-centered">Portfolio by aircraft model</h3>
				<AircraftTable :Aircraft="portfolio.AircraftByModel"
							   IsBlue
							   HideNumber
							   HideEdit
							   Model></AircraftTable>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByModelEmissionPieChart']" :disabled="exporting['aircraftByModelEmissionPieChart']" @click="Export('aircraftByModelEmissionPieChart')">Export chart</b-button>
				<div id="aircraftByModelEmissionPieChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Portfolio by aircraft model showing cumulative CO2 emissions</h3>
					<pie :Data="portfolio.AircraftByModelEmissionPieChart" :Options="chartOptions.pie.modelEmissions.options"></pie>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByModelEmissionScatterChart']" :disabled="exporting['aircraftByModelEmissionScatterChart']" @click="Export('aircraftByModelEmissionScatterChart')">Export chart</b-button>
				<div id="aircraftByModelEmissionScatterChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Portfolio by aircraft model showing aggregate annual CO2 emissions against average stage length</h3>
					<ScatterChart :Data="portfolio.AircraftByModelEmissionScatterChart" :Options="chartOptions.scatter.modelEmissions.options"></ScatterChart>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftByModelEmissionBubbleChart']" :disabled="exporting['aircraftByModelEmissionBubbleChart']" @click="Export('aircraftByModelEmissionBubbleChart')">Export chart</b-button>
				<div id="aircraftByModelEmissionBubbleChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Portfolio by aircraft model showing average cycles against average flight hours against aggregate annual CO2 emissions</h3>
					<bubble :Data="portfolio.AircraftByModelEmissionBubbleChart" :Options="chartOptions.bubble.modelEmissions.options"></bubble>
				</div>
			</div>

			<a class="anchor" id="aircraft"></a>
			<div class="my-6">
				<div class="level">
					<div class="level-left"><h2 class="my-6">Analysis by aircraft</h2></div>
					<div class="level-right"><a class="is-pulled-right" href="#top">Back to top</a></div>
				</div>
				<div class="columns">
					<div class="column">
						<h3>Interactive filters</h3>
						<p>The {{portfolio.Name}} portfolio contains {{portfolio.TotalAircraft}} aircraft entities. Charting in the aircraft analysis section is limited to 50 aircraft but users should decide whether to select fewer aircraft to increase chart legibility.</p>
					</div>
					<div class="column">
						<p class="is-pulled-right">
							<b-button class="is-secondary" @click="interactiveCharting">Select aircraft for charting</b-button>
						</p>
					</div>
				</div>
			</div>
			<div class="columns">
				<div class="column">
					<h3 class="is-blue has-text-centered">List of {{ portfolio.Aircraft.length }} selected aircraft</h3>
					<AircraftTable :Aircraft="portfolio.Aircraft"
								   IsBlue
								   HideNumber
								   HideEdit
								   Model
								   Name></AircraftTable>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftEmissionPieChart']" :disabled="exporting['aircraftEmissionPieChart']" @click="Export('aircraftEmissionPieChart')">Export chart</b-button>
				<div id="aircraftEmissionPieChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Annual CO2 emission by {{ portfolio.Aircraft.length }} selected aircraft</h3>
					<pie :Data="portfolio.AircraftEmissionPieChart" :Options="chartOptions.pie.aircraftEmissions.options" :height="500"></pie>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftEmissionScatterChart']" :disabled="exporting['aircraftEmissionScatterChart']" @click="Export('aircraftEmissionScatterChart')">Export chart</b-button>
				<div id="aircraftEmissionScatterChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Comparison by {{ portfolio.Aircraft.length }} selected aircraft showing aggregate annual CO2 emissions against average stage length</h3>

					<ScatterChart :Data="portfolio.AircraftEmissionScatterChart" :Options="chartOptions.scatter.aircraftEmissions.options"></ScatterChart>
				</div>
			</div>

			<div class="is-chart">
				<b-button class="is-secondary is-pulled-right" :loading="exporting['aircraftEmissionBubbleChart']" :disabled="exporting['aircraftEmissionBubbleChart']" @click="Export('aircraftEmissionBubbleChart')">Export chart</b-button>
				<div id="aircraftEmissionBubbleChart" class="clear">
					<h3 class="chart-title my-6 has-text-centered">Comparison by {{ portfolio.Aircraft.length }} selected aircraft showing average cycles against average flight hours against aggregate annual CO2 emissions</h3>
					<bubble :Data="portfolio.AircraftEmissionBubbleChart" :Options="chartOptions.bubble.aircraftEmissions.options"></bubble>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	var $math;
	import Pie from "@/components/chart/pieChart.vue";
	import ScatterChart from "@/components/chart/scatterChart.vue";
	import Bubble from "@/components/chart/bubbleChart.vue";
	import AircraftTable from "@/components/aircraft/aircraftTable.vue";
	import InteractiveCharting from '@/components/modals/interactiveCharting.vue';
	import portfolioCertificate from "@/components/certificates/portfolioCertificate.vue";
	export default {
		components: {
			AircraftTable,
			"pie": Pie,
			"ScatterChart": ScatterChart,
			"bubble": Bubble,
			'portfolio-certificate': portfolioCertificate
		},
		data() {
			return {
				loading: true,
				exporting: {},
				portfolio: {
					Aircrafts: [],
					AircraftsByType: []
				},
				AllAircraft: [],
				chartOptions: {
					pie: {
						typeEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										label: (item, data) => { return data.labels[item.index] + "  " + $math.format($math.round(data.datasets[0].data[item.index], 3)) + " tonnes" }
									}
								},
								legend: {
									display: false
								}
							}
						},
						modelEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										label: (item, data) => { return data.labels[item.index] + "  " + $math.format($math.round(data.datasets[0].data[item.index], 3)) + " tonnes" }
									}
								},
								legend: {
									display: false
								},

							}
						},
						aircraftEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										label: (item, data) => { return data.labels[item.index] + "  " + $math.format($math.round(data.datasets[0].data[item.index], 3)) + " tonnes" }
									}
								},
								legend: {
									display: false
								},

							}
						}
					},
					scatter: {
						typeEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " stage length | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 3)) + " tonnes" }
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average stage length'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Aggregate annual C02 emissions (tonnes)'
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false
								},
								elements: {
									point: {
										radius: 5
									}
								}
							}
						},
						modelEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " stage length | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 3)) + " tonnes" }
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average stage length'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Aggregate annual C02 emissions (tonnes)'
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false
								},
								elements: {
									point: {
										radius: 5
									}
								}
							}
						},
						aircraftEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => { return $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " stage length | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 3)) + " tonnes" }
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average stage length'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Aggregate annual C02 emissions (tonnes)'
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false
								},
								elements: {
									point: {
										radius: 5
									}
								}
							}
						}
					},
					bubble: {
						typeEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " flight hours | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 2)) + " cycles | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].q, 3)) + " tonnes"
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											labelString: 'Average flight hours'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average cycles'
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false
								},
								elements: {
									point: {
										radius: function (context) {
											var value = context.dataset.data[context.dataIndex];
											const q = value.q / context.dataset.Normalizer;
											if (q < 1)
												return 1;
											else
												return q;
										}
									}
								}
							}
						},
						modelEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " flight hours | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 2)) + " cycles | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].q, 3)) + " tonnes"
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average flight hours'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average cycles'
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false
								},
								elements: {
									point: {
										radius: function (context) {
											var value = context.dataset.data[context.dataIndex];
											const q = value.q / 4500;
											if (q < 1)
												return 1;
											else
												return q;
										}
									}
								}
							}
						},
						aircraftEmissions: {
							options: {
								responsive: true,
								maintainAspectRatio: false,
								tooltips: {
									callbacks: {
										title: (items, data) => data.datasets[items[0].datasetIndex].label,
										label: (item, data) => $math.format($math.round(data.datasets[item.datasetIndex].data[0].x, 2)) + " flight hours | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].y, 2)) + " cycles | " + $math.format($math.round(data.datasets[item.datasetIndex].data[0].q, 3)) + " tonnes"
									}
								},
								scales: {
									xAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average flight hours'
										},
										ticks: {
											suggestedMin: 0
										}
									}],
									yAxes: [{
										scaleLabel: {
											display: true,
											labelString: 'Average cycles'
										},
										ticks: {
											suggestedMin: 0
										}
									}]
								},
								legend: {
									display: false
								},
								elements: {
									point: {
										radius: function (context) {
											var value = context.dataset.data[context.dataIndex];
											const q = value.q / 4500;
											if (q < 1)
												return 1;
											else
												return q;
										}
									}
								}
							}
						}
					}
				},
			}
		},
		computed: {
			aircraftCSV() {
				if (this.portfolio && this.portfolio.Aircraft)
					return this.portfolio.Aircraft.map(a => ({
						'Name': a.Name,
						'Aircraft model': a.Model.Name,
						'Quantity': a.Quantity,
						'Average flight hours': a.FlightHours,
						'Average Cycles': a.Cycles,
						'Aggregate Annual C02 emissions (tonnes)': a.CarbonOutput,
						'Manufacturer': a.Model.Manufacturer.Name,
						'Aircraft Type': a.AircraftType.Name
					}))
				else
					return [];
			},
		},
		methods: {
			calculateTypeEmissionRadius(context) {
				var value = context.dataset.data[context.dataIndex];
				const q = value.q / this.portfolio.AircraftTypeEmissionBubbleChart.Normalizer;
				if (q < 1)
					return 1;
				else
					return q;
			},
			chart(chartApi) {
				//console.log(chartApi);
			},
			download(row) {
				this.setDownloading(row, true);
			},
			setDownloading(row, downloading) {
				this.$set(row, 'downloading', downloading);
			},
			async Export(id) {
				this.$set(this.exporting, id, true);
				await this.$exportWithFilename(this.$moment().format('DD-MM-YYYY') + '-' + id, id);
				this.exporting[id] = false;
			},
			async GetReport() {

				await this.$http.get("/api/portfolio/" + this.$route.params.PortfolioId + "/charts")
					.then(resp => {
						this.AllAircraft = resp.data.Aircraft;
						this.portfolio = this.AddColor(resp.data);
						//console.log("sliced", this.AllAircraft.slice(0, 50));
						this.$set(this.portfolio, 'Aircraft', this.AllAircraft.slice(0, 50));

						this.loading = false;
						return resp;
					}).catch(err => {
						this.loading = false;
						console.error(err);
						return Promise.resolve();
					});
			},
			AddColor(portfolio) {
				for (var i in portfolio.AircraftByTypeEmissionPieChart.datasets) {
					const dataset = portfolio.AircraftByTypeEmissionPieChart.datasets[i];
					dataset.backgroundColor = this.$getColors(2, null);
				}

				for (var i in portfolio.AircraftByTypeEmissionScatterChart.datasets) {
					const dataset = portfolio.AircraftByTypeEmissionScatterChart.datasets[i];
					dataset.borderColor = this.$getColors(2, i);
					dataset.backgroundColor = this.$getColors(2, i);
					dataset.borderWidth = 2;
				}

				for (var i in portfolio.AircraftByTypeEmissionBubbleChart.datasets) {
					const dataset = portfolio.AircraftByTypeEmissionBubbleChart.datasets[i];
					dataset.borderColor = this.$getColors(2, i);
					dataset.backgroundColor = this.$getColors(2, i, 0.4);
					dataset.borderWidth = 1;
				}

				for (var i in portfolio.AircraftByModelEmissionPieChart.datasets) {
					const dataset = portfolio.AircraftByModelEmissionPieChart.datasets[i];
					dataset.backgroundColor = this.$getColors(2, null);
				}

				for (var i in portfolio.AircraftByModelEmissionScatterChart.datasets) {
					const dataset = portfolio.AircraftByModelEmissionScatterChart.datasets[i];
					dataset.borderColor = this.$getColors(2, i);
					dataset.backgroundColor = this.$getColors(2, i);
					dataset.borderWidth = 2;
				}

				for (var i in portfolio.AircraftByModelEmissionBubbleChart.datasets) {
					const dataset = portfolio.AircraftByModelEmissionBubbleChart.datasets[i];
					dataset.borderColor = this.$getColors(2, i);
					dataset.backgroundColor = this.$getColors(2, i, 0.4);
					dataset.borderWidth = 1;
				}

				for (var i in portfolio.AircraftEmissionPieChart.datasets) {
					const dataset = portfolio.AircraftEmissionPieChart.datasets[i];
					dataset.backgroundColor = this.$getColors(2, null);
				}

				for (var i in portfolio.AircraftEmissionScatterChart.datasets) {
					const dataset = portfolio.AircraftEmissionScatterChart.datasets[i];
					dataset.borderColor = this.$getColors(2, i);
					dataset.backgroundColor = this.$getColors(2, i);
					dataset.borderWidth = 2;
				}

				for (var i in portfolio.AircraftEmissionBubbleChart.datasets) {
					const dataset = portfolio.AircraftEmissionBubbleChart.datasets[i];
					dataset.borderColor = this.$getColors(2, i);
					dataset.backgroundColor = this.$getColors(2, i, 0.4);
					dataset.borderWidth = 1;
				}

				return portfolio;
			},
			interactiveCharting() {
				let id = this.$route.params.PortfolioId;
				this.$buefy.modal.open({
					parent: this,
					component: InteractiveCharting,
					hasModalCard: true,
					props: {
						PortfolioId: id
					},
					events: {
						'selectAircraft': async (ids) => {
							await this.$http.post("/api/portfolio/selected/charts", ids)
								.then(resp => {
									let newPortfolio = resp.data;

									for (var i in newPortfolio.AircraftEmissionPieChart.datasets) {
										const dataset = newPortfolio.AircraftEmissionPieChart.datasets[i];
										dataset.backgroundColor = this.$getColors(0, null);
									}

									for (var i in newPortfolio.AircraftEmissionScatterChart.datasets) {
										const dataset = newPortfolio.AircraftEmissionScatterChart.datasets[i];
										dataset.borderColor = this.$getColors(0, i);
										dataset.backgroundColor = this.$getColors(0, i);
										dataset.borderWidth = 2;
									}

									for (var i in newPortfolio.AircraftEmissionBubbleChart.datasets) {
										const dataset = newPortfolio.AircraftEmissionBubbleChart.datasets[i];
										dataset.borderColor = this.$getColors(0, i);
										dataset.backgroundColor = this.$getColors(0, i, 0.4);
										dataset.borderWidth = 1;
									}
									this.$set(this.portfolio, 'AircraftEmissionPieChart', newPortfolio.AircraftEmissionPieChart);
									this.$set(this.portfolio, 'AircraftEmissionScatterChart', newPortfolio.AircraftEmissionScatterChart);
									this.$set(this.portfolio, 'AircraftEmissionBubbleChart', newPortfolio.AircraftEmissionBubbleChart);
									//console.log(newPortfolio.Aircraft)
									this.$set(this.portfolio, 'Aircraft', newPortfolio.Aircraft);
									this.loading = false;
									return resp;
								}).catch(err => {
									this.loading = false;
									console.error(err);
									return Promise.resolve();
								});
						}
					}
				})
			}
		},
		mounted() {
			$math = this.$math;
			this.GetReport();
		}
	}
</script>
